import { useState } from "react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import Editable from "@/components/Editable";
import EditableSection from "@/components/blocks/EditableSection";
import { Plus, Minus } from "@phosphor-icons/react";

const FAQS = [
  { q: "How old does my puppy need to be to attend Puppy School?", a: "Puppies must be at least 8 weeks old. Puppy School is for puppies 8 to 14 weeks. From 14 weeks onwards, your puppy joins Teenage Puppy School." },
  { q: "What vaccinations does my puppy need?", a: "For Puppy School (8 to 14 weeks) puppies must have had at least one vaccination. For Teenage Puppy School (14 weeks +) puppies must have had at least two vaccinations. If you're unsure, confirm with your vet before booking." },
  { q: "How much is Puppy School?", a: "Puppy School is $149 AUD for 4 weeks of classes (45 minutes per class). Maximum 6 puppies per class for personalised attention. Includes a free treat pouch and access to our online video library." },
  { q: "Where are classes held?", a: "We run group puppy school at three convenient Sunshine Coast halls: Mudjimba Community Hall, Kawana Scouts Hall (Buddina) and Eudlo Hall. See our Service Areas page for full details." },
  { q: "Do you cover my suburb for private sessions?", a: "the team travels across the Sunshine Coast — North, Central and Hinterland — for private puppy consults, walk & trains and home check-ins. Use the postcode lookup on our Service Areas page to confirm." },
  { q: "What if my dog has behaviour issues like reactivity or aggression?", a: "Severe behaviour concerns are best addressed through Private Training. Please email support@puppl.app with your suburb, dog's age and a brief description, and the team will personally review your enquiry." },
  { q: "Do you offer online courses?", a: "Yes — visit our Online Courses page for self-paced video training you can watch from anywhere. Topics range from foundations to fun tricks and activities." },
  { q: "What training methods do you use?", a: "Non-aversive only. We are 100% fear-free and evidence-based. No fear, no force, no shock or prong collars — ever." },
  { q: "How do I book a Therapy Dog assessment?", a: "Therapy Dog assessments are by Wishlist referral only. Please contact Wishlist first at therapydogs@wishlist.org to express your interest. Once referred, the team runs the assessment for the Wishlist Therapy Dog Program at SCUH." },
  { q: "What if I need to cancel or reschedule?", a: "Get in touch as early as possible via email or phone. See our Terms and Conditions for the full cancellation policy." },
];

export default function Faq() {
  const [open, setOpen] = useState(0);
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="FAQ | Puppl Sunshine Coast Puppy Training"
        description="Frequently asked questions about Sunshine Coast puppy training — Puppy School ages, vaccinations, pricing, service areas, training methods and more."
        canonical={typeof window !== "undefined" ? window.location.origin + "/faq" : ""}
        schema={[
          faqSchema,
          breadcrumbSchema([
            { name: "Home", url: "https://puppl.app/" },
            { name: "FAQ", url: "https://puppl.app/faq" },
          ]),
        ]}
      />
      <PublicHeader />
      <EditableSection slug="faq" />

      <section className="bg-[#1b4a76] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20 text-center">
          <Editable id="faq.eyebrow" as="p" className="text-sm uppercase tracking-wider font-semibold text-[#E2B054] mb-3" defaultHtml="FAQ" />
          <Editable id="faq.heading" as="h1" className="font-heading text-4xl lg:text-6xl font-bold leading-tight mb-3" defaultHtml="Quick answers." />
          <Editable id="faq.subtitle" as="p" className="text-lg lg:text-xl opacity-90" defaultHtml="Everything you might want to know before getting started." />
        </div>
      </section>

      <section className="py-16 px-6 lg:px-12 max-w-4xl mx-auto">
        <div className="space-y-3">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="card-pp" data-testid={`faq-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full text-left px-6 py-5 flex items-start justify-between gap-4"
                >
                  <Editable id={`faq.item.${i}.q`} as="span" className="font-heading font-semibold text-stone-900 text-lg" defaultHtml={item.q} />
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#f38838]/15 flex items-center justify-center text-[#f38838]">
                    {isOpen ? <Minus size={16} weight="bold" /> : <Plus size={16} weight="bold" />}
                  </span>
                </button>
                {isOpen && (
                  <Editable
                    id={`faq.item.${i}.a`}
                    as="div"
                    className="px-6 pb-5 text-stone-700 leading-relaxed"
                    defaultHtml={item.a}
                    testid={`faq-${i}-answer`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </section>

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}
